import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Dog, Camera, Heart, Trophy, PawPrint, ClipboardEdit, Info, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../utils/supabase';
import { PetRegistrationModal } from './PetRegistrationModal';
import { PetCard, type Pet } from './PetCard';
import { getVoterKey, loadVotedIds, persistVotedIds } from '../utils/petWall';
// Home preview shows only the top N pets; the full wall lives at /muro.
const HOME_LIMIT = 12;
export function PetWallSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const topCtaRef = useRef<HTMLDivElement | null>(null);
  const [isSectionInView, setIsSectionInView] = useState(false);
  const [isTopCtaInView, setIsTopCtaInView] = useState(false);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsSectionInView(entry.isIntersecting);
    }, {
      threshold: 0.05
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const node = topCtaRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsTopCtaInView(entry.isIntersecting);
    }, {
      threshold: 0.4
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // Sticky bottom CTA only shows on mobile when section is visible but the
  // top inline CTA is already out of view — avoids dual-button conflict.
  const showStickyBottomCta = isSectionInView && !isTopCtaInView;
  // Live pet wall — fed from the `public_pet_wall` view (only paid pets w/ photo).
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [votedIds, setVotedIds] = useState<Set<string>>(() => loadVotedIds());
  const fetchPets = async () => {
    const {
      data,
      error
    } = await supabase.from('public_pet_wall').select('id, name, breed, bio, photo_url, huellas').order('huellas', {
      ascending: false
    });
    if (!error && data) {
      setPets(data.map(row => ({
        id: row.id,
        name: row.name,
        breed: row.breed,
        bio: row.bio,
        imageUrl: row.photo_url,
        huellas: row.huellas ?? 0
      })));
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchPets();
    // Realtime — refresh huellas / new pets without reload
    const channel = supabase.channel('pet_wall_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'pet_huellas'
    }, () => fetchPets()).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'registration_pets'
    }, () => fetchPets()).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
  const handleDarHuella = async (id: string) => {
    if (votedIds.has(id)) {
      toast.info('Ya le diste tu huella a esta mascota 🐾');
      return;
    }
    // Optimistic update
    setPets(prev => prev.map(pet => pet.id === id ? {
      ...pet,
      huellas: pet.huellas + 1
    } : pet));
    const nextVoted = new Set(votedIds).add(id);
    setVotedIds(nextVoted);
    persistVotedIds(nextVoted);
    const {
      error
    } = await supabase.from('pet_huellas').insert({
      pet_id: id,
      voter_key: getVoterKey()
    });
    if (error && error.code !== '23505') {
      // Real failure — roll back the optimistic +1
      setPets(prev => prev.map(pet => pet.id === id ? {
        ...pet,
        huellas: Math.max(0, pet.huellas - 1)
      } : pet));
      const reverted = new Set(nextVoted);
      reverted.delete(id);
      setVotedIds(reverted);
      persistVotedIds(reverted);
      toast.error('No pudimos registrar tu huella. Intenta de nuevo.');
    }
  };
  const topPets = [...pets].sort((a, b) => b.huellas - a.huellas).slice(0, 5);
  return <section ref={sectionRef} id="muro" className="py-24 relative overflow-hidden" data-id="element-879">
      <PetRegistrationModal open={modalOpen} onClose={() => setModalOpen(false)} data-id="element-880" />
      {/* Paw prints background image */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-881">
        <img src="https://cdn.magicpatterns.com/uploads/mVfgFvxDgxJwQdHZBf1guJ/PATRON_HUELLAS_fondo.png" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-882" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-883">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-884">
          <h2 className="text-4xl md:text-5xl text-brand-navy mb-6" data-id="element-885">
            {['El', 'Muro', 'de', 'las', 'Huellas'].map((word, i) => <motion.span key={i} initial={{
            opacity: 0,
            y: 30,
            filter: 'blur(10px)'
          }} whileInView={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="inline-block shimmer-text mr-3" data-id="element-886">
                {word}
              </motion.span>)}
          </h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg text-gray-600 mb-10" data-id="element-887">
            Sube la foto de tu mascota, acumula Huellas y compite por ser la{' '}
            <strong data-id="element-888">Mascota Influencer 2026</strong>.
          </motion.p>

          {/* 3 Steps */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex flex-row justify-center items-start gap-2 md:gap-10" data-id="element-889">
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-890">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-navy/10 flex items-center justify-center text-brand-navy mb-2 md:mb-3" data-id="element-891">
                <Dog className="w-6 h-6 md:w-8 md:h-8" data-id="element-892" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-893">
                1. Regístralo
              </span>
            </div>
            <div className="hidden md:block w-12 h-0.5 bg-gray-200 mt-7" data-id="element-894"></div>
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-895">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-cyan/10 flex items-center justify-center text-brand-cyan mb-2 md:mb-3" data-id="element-896">
                <Camera className="w-6 h-6 md:w-8 md:h-8" data-id="element-897" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-898">
                2. Súbelo
              </span>
            </div>
            <div className="hidden md:block w-12 h-0.5 bg-gray-200 mt-7" data-id="element-899"></div>
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-900">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-yellow/10 flex items-center justify-center text-brand-yellow mb-2 md:mb-3" data-id="element-901">
                <PawPrint className="w-6 h-6 md:w-8 md:h-8" data-id="element-902" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-903">
                3. Huellas
              </span>
              <span className="hidden md:block text-xs text-gray-500 mt-1" data-id="element-904">
                1 Like = 1 Huella
              </span>
            </div>
            <div className="hidden md:block w-12 h-0.5 bg-gray-200 mt-7" data-id="element-905"></div>
            <div className="flex flex-col items-center flex-1 md:flex-initial" data-id="element-906">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green mb-2 md:mb-3" data-id="element-907">
                <Trophy className="w-6 h-6 md:w-8 md:h-8" data-id="element-908" />
              </div>
              <span className="font-semibold text-brand-navy text-[11px] md:text-base text-center leading-tight" data-id="element-909">
                4. ¡Gana!
              </span>
              <span className="hidden md:block text-xs text-gray-500 mt-1" data-id="element-910">
                Mascota Influencer
              </span>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div ref={topCtaRef} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="mt-10" data-id="element-911">
            <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-3 bg-brand-cyan hover:bg-blue-700 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-full font-bold text-base md:text-lg transition-all shadow-lg hover:shadow-cyan-500/30 transform hover:-translate-y-1" data-id="element-912">
              <ClipboardEdit className="w-5 h-5 md:w-6 md:h-6" data-id="element-913" />
              Registra a tu mascota
            </button>
          </motion.div>
        </div>

        {/* NEW: Info banner */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-10 mx-auto max-w-3xl bg-brand-green/10 border border-brand-green/30 rounded-2xl p-5 flex items-start gap-3" data-id="element-914">
          <Info className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-915" />
          <p className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-916">
            <strong className="text-brand-green" data-id="element-917">
              ¿Ya te inscribiste a la Caminata Canina?
            </strong>{' '}
            Tus mascotas aparecen aquí automáticamente 🎉. Si aún no lo has
            hecho,{' '}
            <Link to="/caminata-canina" className="text-brand-green font-bold underline hover:text-brand-navy transition-colors" data-id="element-918">
              inscríbete a la Caminata
            </Link>{' '}
            para sumar a tu peludo al Muro.
          </p>
        </motion.div>

        {/* Mobile-only podium Top 5 */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="lg:hidden mb-6 bg-brand-navy rounded-2xl p-4 shadow-xl" data-id="element-919">
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-920">
            <Trophy className="w-5 h-5 text-brand-yellow" data-id="element-921" />
            <h3 className="text-base font-bold text-white" data-id="element-922">
              Top 5 Influencers
            </h3>
          </div>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1" data-id="element-923">
            {topPets.map((pet, index) => <div key={pet.id} className="flex-shrink-0 flex flex-col items-center w-16" data-id="element-924">
                <div className="relative" data-id="element-925">
                  <div className={`w-14 h-14 rounded-full bg-gray-100 border-2 ${index === 0 ? 'border-brand-yellow' : 'border-white/20'} overflow-hidden`} data-id="element-926">
                    {pet.imageUrl && <img src={pet.imageUrl} alt={pet.name} className="w-full h-full object-cover" data-id="element-927" />}
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-brand-yellow text-brand-navy text-[10px] font-bold flex items-center justify-center shadow" data-id="element-928">
                    {index + 1}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-white mt-1.5 truncate w-full text-center" data-id="element-929">
                  {pet.name}
                </span>
                <span className="text-[10px] text-brand-cyan font-bold" data-id="element-930">
                  {pet.huellas}
                </span>
              </div>)}
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-10" data-id="element-931">
          {/* Main Grid (Instagram style) */}
          <div className="w-full lg:w-2/3" data-id="element-932">
            {loading ? <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" data-id="element-933">
                {Array.from({
              length: 6
            }).map((_, i) => <div key={i} className="bg-white/60 rounded-2xl border border-gray-200 overflow-hidden animate-pulse" data-id="element-934">
                    <div className="w-full aspect-square bg-gray-200" data-id="element-935" />
                    <div className="p-2.5 md:p-4 space-y-2" data-id="element-936">
                      <div className="h-3 bg-gray-200 rounded w-2/3" data-id="element-937" />
                      <div className="h-7 bg-gray-200 rounded" data-id="element-938" />
                    </div>
                  </div>)}
              </div> : pets.length === 0 ? <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200 p-10 text-center" data-id="element-939">
                <div className="w-16 h-16 rounded-full bg-brand-cyan/10 flex items-center justify-center mx-auto mb-4" data-id="element-940">
                  <PawPrint className="w-8 h-8 text-brand-cyan" data-id="element-941" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2" data-id="element-942">
                  Aún no hay mascotas en el Muro
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-943">
                  Sé el primero en sumar a tu peludo. Inscríbete a la Caminata
                  Canina y tu mascota aparecerá aquí automáticamente.
                </p>
                <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white px-6 py-3 rounded-full font-bold transition-all shadow-lg" data-id="element-944">
                  <ClipboardEdit className="w-5 h-5" data-id="element-945" /> Registra a tu mascota
                </button>
              </div> : <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" data-id="element-946">
                  {pets.slice(0, HOME_LIMIT).map((pet, index) => <PetCard key={pet.id} pet={pet} index={index} hasVoted={votedIds.has(pet.id)} onVote={handleDarHuella} data-id="element-947" />)}
                </div>
                {pets.length > HOME_LIMIT && <div className="mt-8 text-center" data-id="element-948">
                    <Link to="/muro" className="inline-flex items-center gap-2 bg-brand-navy hover:bg-[#1a2a8a] text-white px-8 py-3.5 rounded-full font-bold text-base md:text-lg transition-all shadow-lg hover:-translate-y-0.5" data-id="element-949">
                      Ver todas las mascotas
                      <ArrowRight className="w-5 h-5" data-id="element-950" />
                    </Link>
                  </div>}
              </>}
          </div>

          {/* Sidebar Ranking (desktop only) */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="w-full lg:w-1/3 hidden lg:block" data-id="element-951">
            <div className="bg-brand-navy rounded-3xl p-6 shadow-xl sticky top-28" data-id="element-952">
              <div className="flex gap-3 mb-6 border-b border-white/10 pb-4 items-center justify-center" data-id="element-953">
                <Trophy className="w-8 h-8 text-brand-yellow" data-id="element-954" />
                <h3 className="text-2xl font-bold text-white" data-id="element-955">
                  Top 5 Influencers
                </h3>
              </div>

              <div className="space-y-4 mb-8" data-id="element-956">
                {topPets.map((pet, index) => <div key={pet.id} className="flex items-center gap-4 bg-white/5 rounded-xl p-3 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-957">
                    <div className="w-8 text-center font-bold text-brand-yellow text-xl" data-id="element-958">
                      #{index + 1}
                    </div>
                    <div className={`w-12 h-12 rounded-full bg-gray-100 border-2 border-white/20 flex-shrink-0 overflow-hidden`} data-id="element-959">
                      {pet.imageUrl && <img src={pet.imageUrl} alt={pet.name} className="w-full h-full object-cover" data-id="element-960" />}
                    </div>
                    <div className="flex-grow" data-id="element-961">
                      <h4 className="font-bold text-white text-sm" data-id="element-962">
                        {pet.name}
                      </h4>
                      <p className="text-xs text-white/60" data-id="element-963">{pet.breed}</p>
                    </div>
                    <div className="text-right" data-id="element-964">
                      <span className="font-bold text-brand-cyan text-sm" data-id="element-965">
                        {pet.huellas}
                      </span>
                      <p className="text-[10px] text-white/50 uppercase" data-id="element-966">
                        Huellas
                      </p>
                    </div>
                  </div>)}
              </div>

              <button onClick={() => setModalOpen(true)} className="w-full block text-center bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-xl font-bold text-lg transition-colors shadow-lg" data-id="element-967">
                Registra a tu mascota
              </button>
            </div>
          </motion.div>
        </div>

        {/* Green Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-16 bg-brand-green/10 border border-brand-green/20 rounded-2xl p-6 text-center max-w-3xl mx-auto" data-id="element-968">
          <p className="text-brand-green flex items-center justify-center gap-2 text-base md:text-2xl font-bold leading-snug" data-id="element-969">
            <Heart className="w-5 h-5 md:w-6 md:h-6 fill-brand-green flex-shrink-0" data-id="element-970" />
            <span data-id="element-971">
              Parte de cada inscripción apoya al CEIBA Rionegro, nuestra
              fundación aliada 2026.
            </span>
          </p>
        </motion.div>
      </div>

      {/* Floating pill CTA (mobile only, when section is in view AND top CTA is out of view).
                  Anchored bottom-left with right-side padding to leave room for the WhatsApp FAB. */}
      <AnimatePresence data-id="element-972">
        {showStickyBottomCta && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="md:hidden fixed bottom-6 left-4 right-24 z-40 pointer-events-none" data-id="element-973">
            <button onClick={() => setModalOpen(true)} className="pointer-events-auto w-full inline-flex items-center justify-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white px-5 py-3.5 rounded-full font-bold text-sm transition-all shadow-[0_8px_24px_rgba(0,188,212,0.45)]" data-id="element-974">
              <ClipboardEdit className="w-5 h-5" data-id="element-975" />
              Registra a tu mascota
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}